import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { SnapshotBar } from "./Metrics";

function RoadmapField({ k, label, big, testid, value, editable, onChange }) {
  return (
    <div className={`tb-rd-field ${big ? "big" : ""}`}>
      <label>
        <span className="tb-rd-label">{label}</span>
      </label>
      {editable ? (
        <textarea value={value || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={testid} rows={big ? 3 : 2} />
      ) : (
        <p className="tb-rd-readonly">{value || "—"}</p>
      )}
    </div>
  );
}

export default function Roadmap({ targetUserId, hideHeader, snapshotInline }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);

  const isOwn = !targetUserId || targetUserId === user?.id;
  const editable = isOwn && user?.role === "entrepreneur";

  useEffect(() => {
    const q = !isOwn ? `?user_id=${targetUserId}` : "";
    api.get(`/roadmap${q}`).then((r) => setData(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get(`/snapshots/roadmap${q}`).then((r) => setSnaps(r.data)).catch(() => setSnaps([]));
  }, [targetUserId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !user) return <div className="tb-loading">{t("common.loading")}</div>;

  const set = (k, v) => {
    setData({ ...data, [k]: v });
    setSaved(false);
  };
  const setQ = (i, v) => {
    const q = [...(data.quarters || ["", "", "", ""])];
    q[i] = v;
    setData({ ...data, quarters: q });
    setSaved(false);
  };

  const save = async () => {
    setErr("");
    try {
      await api.put("/roadmap", {
        north_star: data.north_star,
        year_10: data.year_10,
        year_5: data.year_5,
        year_2: data.year_2,
        year_1: data.year_1,
        quarters: data.quarters,
        month_1: data.month_1,
        week_2: data.week_2,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="tb-page">
      {!hideHeader && (
        <>
          <h1 className="tb-h1" data-testid="roadmap-heading">{t("roadmap.title")}</h1>
          <p className="tb-muted" style={{ marginBottom: "1.5rem" }}>{t("roadmap.subtitle")}</p>
        </>
      )}

      {(editable || snapshotInline) && (
        <SnapshotBar
          kind="roadmap"
          snaps={snaps}
          canSave={editable}
          busy={snapBusy}
          t={t}
          userId={isOwn ? undefined : targetUserId}
          onSave={async () => {
            setSnapBusy(true);
            try {
              // Persist edits first so the snapshot reflects current state
              if (editable) await save();
              await api.post("/snapshots/roadmap");
              api.get("/snapshots/roadmap").then((r) => setSnaps(r.data)).catch(() => {});
            } finally { setSnapBusy(false); }
          }}
        />
      )}

      <div className="tb-rd-stack">
        <div className="tb-rd-northstar">
          <span className="tb-rd-pill">⭐ {t("roadmap.northStar")}</span>
          {editable ? (
            <textarea
              value={data.north_star || ""}
              onChange={(e) => set("north_star", e.target.value)}
              placeholder={t("roadmap.northStarHelp")}
              rows={2}
              data-testid="roadmap-north-star"
            />
          ) : (
            <p className="tb-rd-readonly big">{data.north_star || "—"}</p>
          )}
        </div>

        <div className="tb-rd-row">
          <RoadmapField k="year_10" label={t("roadmap.year10")} testid="roadmap-y10" value={data.year_10} editable={editable} onChange={set} />
          <RoadmapField k="year_5" label={t("roadmap.year5")} testid="roadmap-y5" value={data.year_5} editable={editable} onChange={set} />
          <RoadmapField k="year_2" label={t("roadmap.year2")} testid="roadmap-y2" value={data.year_2} editable={editable} onChange={set} />
          <RoadmapField k="year_1" label={t("roadmap.year1")} testid="roadmap-y1" value={data.year_1} editable={editable} onChange={set} big />
        </div>

        <div className="tb-rd-quarters">
          <h3 className="tb-h2" style={{ marginTop: 0 }}>{t("roadmap.quarters")}</h3>
          <div className="tb-rd-row">
            {[0, 1, 2, 3].map((i) => (
              <div className="tb-rd-field" key={i}>
                <label><span className="tb-rd-label">Q{i + 1}</span></label>
                {editable ? (
                  <textarea value={(data.quarters || [])[i] || ""} onChange={(e) => setQ(i, e.target.value)} rows={2} data-testid={`roadmap-q${i + 1}`} />
                ) : (
                  <p className="tb-rd-readonly">{(data.quarters || [])[i] || "—"}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="tb-rd-row">
          <RoadmapField k="month_1" label={t("roadmap.month1")} testid="roadmap-m1" value={data.month_1} editable={editable} onChange={set} big />
          <RoadmapField k="week_2" label={t("roadmap.week2")} testid="roadmap-w2" value={data.week_2} editable={editable} onChange={set} big />
        </div>

        {editable && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: "1rem" }}>
            <button className="tb-primary-btn" onClick={save} data-testid="roadmap-save">{t("roadmap.save")}</button>
            {saved && <span className="tb-badge good" data-testid="roadmap-saved">✓ {t("roadmap.saved")}</span>}
            {err && <span className="tb-form-error" style={{ padding: "6px 12px" }}>{err}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
